import React from 'react';
import { Box } from '@mui/material';
import { Button } from 'components/Buttons';
import { CustomIcon } from 'components/CustomIcon';
import { Typography } from 'components/Typography';
import { pxToEm } from 'theme';
import * as Styled from './BannerInfoRecommendationModal.styles';
import { RecommendationModalProps } from './RecommendationModal';

export type ProposalType =
  | 'dealer'
  | 'service'
  | 'dealer-added'
  | 'ask-for-dealer';

export type SplitBannersAdditionalProps = {
  labels: {
    bannerLabel: string | JSX.Element;
    bannerShippingTitle: string;
    bannerShippingSubtitle?: string | null;
    bottomBannerTitle: string;
    bottomBannerSubtitle: string;
  };

  controlRuleId: string | number | null;
  proposal?: {
    title: string;
    subtitle: string | string[];
    companyName: string;
    companyImage: string;
    type?: ProposalType;
    yesLabel?: string;
    noLabel?: string;
    textFieldLabel?: string;
    isAddDealerButtonLoading?: boolean;
    onProposalTextFieldChange?: (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => void;
    onYesClick: () => void;
    onNoClick: () => void;
  } | null;
};

type BannerInfoRecommendationModalProps = RecommendationModalProps &
  SplitBannersAdditionalProps;

export const BannerInfoRecommendationModal: React.FC<
  BannerInfoRecommendationModalProps
> = ({
  isOpen,
  backdropTopPadding,
  recommendedItems,
  labels: {
    bannerLabel,
    bannerShippingTitle,
    bannerShippingSubtitle,
    bottomBannerTitle,
    bottomBannerSubtitle,
  },
  proposal,
  controlRuleId,
  addedMaterialImage,
  onMouseOver,
  onRecommendedItemUrlClick,
  onAddToCart,
  onClose,
}) => {
  const isProposalActive = proposal != null;
  const proposalType = proposal?.type || 'service';
  const yesLabel = proposal?.yesLabel || 'Yes, contact me';
  const noLabel = proposal?.noLabel || 'No, thanks';
  const textFieldLabel = proposal?.textFieldLabel || '3M Partner/Supplier Name';
  const isAddDealerButtonLoading = proposal?.isAddDealerButtonLoading ?? false;

  const proposalContentByType = {
    service: (
      <Styled.TopBannerContainer id="sm-diverzify" onMouseOver={onMouseOver}>
        <Typography fontSize="18px" lineHeight="32px" fontWeight={600}>
          {proposal?.title}
        </Typography>
        {proposal?.subtitle != null && (
          <Box mb="16px">
            {Array.isArray(proposal?.subtitle) ? (
              proposal?.subtitle.map((subtitleItem) => (
                <li key={subtitleItem}>{subtitleItem}</li>
              ))
            ) : (
              <Typography>{proposal?.subtitle}</Typography>
            )}
          </Box>
        )}
        <div className="flex flex-row gap-4 w-full">
          <Styled.MaterialImageLinkWrapper>
            <Styled.MaterialImage
              src={proposal?.companyImage}
              width={88}
              height={88}
              alt=""
            />
          </Styled.MaterialImageLinkWrapper>
          <div className="flex flex-col gap-2 w-full justify-center">
            <Typography
              fontWeight={400}
              fontSize="13px"
              color="primary"
              width="100%"
              overflow="hidden"
            >
              {proposal?.companyName}
            </Typography>
            <Typography
              fontWeight={700}
              fontSize={pxToEm(16)}
              color="primary"
              whiteSpace="nowrap"
              width="100%"
              overflow="hidden"
              textOverflow="ellipsis"
              display="flex"
              gap="8px"
            >
              {proposal?.onYesClick != null && (
                <Button
                  id="sm-diverzify-yes"
                  onClick={() => proposal?.onYesClick()}
                  variant="contained"
                  color="primary"
                >
                  {yesLabel}
                </Button>
              )}
              {proposal?.onNoClick != null && (
                <Button
                  id="sm-diverzify-no"
                  onClick={() => proposal?.onNoClick()}
                  variant="contained"
                  color="secondary"
                >
                  {noLabel}
                </Button>
              )}
            </Typography>
          </div>
        </div>
      </Styled.TopBannerContainer>
    ),
    dealer: (
      <>
        <Styled.Divider />
        <Typography
          mt="16px"
          fontSize="17px"
          lineHeight="32px"
          fontWeight={600}
        >
          {proposal?.title}
        </Typography>
        <Typography mb="20px">{proposal?.subtitle}</Typography>
        <div className="flex flex-row gap-4 w-full">
          <Styled.MaterialImageLinkWrapper>
            <Styled.MaterialImage
              src={proposal?.companyImage}
              width={88}
              height={88}
              alt=""
            />
          </Styled.MaterialImageLinkWrapper>
          <div className="flex flex-col gap-2 w-full justify-center">
            <Box position="relative">
              <Styled.TextField
                label={textFieldLabel}
                onChange={proposal?.onProposalTextFieldChange}
                type="text"
                fullWidth
                variant="standard"
                autoComplete="off"
              />
              <Styled.InputAddButton
                tabIndex={0}
                variant="contained"
                color="primary"
                onPointerDown={proposal?.onYesClick}
                disabled={isAddDealerButtonLoading}
              >
                Add
              </Styled.InputAddButton>
            </Box>
          </div>
        </div>
      </>
    ),
    'dealer-added': (
      <>
        <Styled.Divider />
        <Box
          mt="16px"
          width="100%"
          display="flex"
          padding="43px 96px"
          justifyContent="center"
          alignItems="center"
        >
          <Box display="flex" flexDirection="column" alignItems="center">
            <CustomIcon size={1.32} dense name="outlined-circle-check" />
            <Typography mt="12px" textAlign="center" fontSize="13px">
              {proposal?.subtitle}
            </Typography>
          </Box>
        </Box>
      </>
    ),
    'ask-for-dealer': (
      <>
        <Styled.Divider />
        <Typography
          fontSize="17px"
          lineHeight="32px"
          mt="16px"
          mb="20px"
          fontWeight={600}
        >
          {proposal?.title}
        </Typography>
        <div className="flex flex-row gap-4 w-full">
          <Styled.MaterialImageLinkWrapper>
            <Styled.MaterialImage
              src={proposal?.companyImage}
              width={88}
              height={88}
            />
          </Styled.MaterialImageLinkWrapper>
          <div className="flex flex-col gap-2 w-full justify-center">
            <Typography
              fontWeight={700}
              fontSize={pxToEm(16)}
              color="primary"
              whiteSpace="nowrap"
              width="100%"
              overflow="hidden"
              textOverflow="ellipsis"
            >
              {proposal?.companyName}
            </Typography>
            <Typography
              fontWeight={700}
              fontSize={pxToEm(16)}
              color="primary"
              whiteSpace="nowrap"
              width="100%"
              overflow="hidden"
              textOverflow="ellipsis"
              display="flex"
              gap="8px"
            >
              <Button
                onClick={() => proposal?.onNoClick()}
                variant="contained"
                color="secondary"
              >
                {noLabel}
              </Button>
              <Button
                onClick={() => proposal?.onYesClick()}
                variant="contained"
                color="primary"
              >
                {yesLabel}
              </Button>
            </Typography>
          </div>
        </div>
      </>
    ),
  };

  const shouldShowImage = !controlRuleId && addedMaterialImage;

  return (
    <Styled.Dialog
      id="rm-modal-top-banner"
      open={isOpen}
      onClose={onClose}
      dialogWidth={440}
      shouldDisableBottomPadding
      backdropTopPadding={backdropTopPadding}
    >
      <Styled.TopBannerContainer onMouseOver={onMouseOver}>
        <Styled.CloseButton
          onClick={onClose}
          dense
          iconName="close"
          size="medium"
          color="inherit"
        />
        <div
          className="grid gap-4 w-full items-start"
          style={{
            gridTemplateColumns: 'auto 1fr',
          }}
        >
          {/* Show product image when item was successfully added (no control rule restrictions) */}
          {shouldShowImage ? (
            <Styled.SmallerMaterialImageLinkWrapper>
              <Styled.MaterialImage
                src={addedMaterialImage}
                width={56}
                height={56}
                alt=""
              />
            </Styled.SmallerMaterialImageLinkWrapper>
          ) : null}
          <div className="flex flex-col gap-2 w-full">
            <Styled.MaterialInfo>
              {typeof bannerLabel === 'string' ? (
                <Styled.SpanLabel>{bannerLabel}</Styled.SpanLabel>
              ) : (
                bannerLabel
              )}
              <Typography mt="8px" fontWeight={600}>
                {bannerShippingTitle}
              </Typography>
            </Styled.MaterialInfo>
            {bannerShippingSubtitle != null && (
              <Box>
                {typeof bannerShippingSubtitle === 'string' ? (
                  <Typography>{bannerShippingSubtitle}</Typography>
                ) : (
                  bannerShippingSubtitle
                )}
              </Box>
            )}
          </div>
        </div>
        {isProposalActive &&
          proposalType !== 'service' &&
          proposalContentByType[proposalType]}
      </Styled.TopBannerContainer>
      {recommendedItems.length > 0 && (
        <Styled.TopBannerContainer onMouseOver={onMouseOver}>
          <Typography fontSize="1.1em" fontWeight={600}>
            {bottomBannerTitle}
          </Typography>
          <Typography mb="20px">{bottomBannerSubtitle}</Typography>
          <Styled.RecommendedItemsContainer
            className="flex flex-col gap-4"
            onMouseOver={onMouseOver}
          >
            {recommendedItems.map((recommendedItem) => (
              <div key={recommendedItem.id} className="flex flex-row gap-4">
                <Styled.MaterialImageLinkWrapper
                  onPointerDown={() =>
                    onRecommendedItemUrlClick?.(recommendedItem)
                  }
                  target="_blank"
                  href={recommendedItem.linkUrl}
                  rel="noreferrer"
                >
                  <Styled.MaterialImage
                    src={recommendedItem.image}
                    width={88}
                    height={88}
                  />
                </Styled.MaterialImageLinkWrapper>
                <div className="flex flex-row justify-between items-center gap-2 w-full">
                  <Styled.MaterialInfo
                    className="flex flex-col gap-1 truncate"
                    style={{ maxWidth: '220px' }}
                  >
                    <Typography
                      fontWeight={700}
                      fontSize="0.65em"
                      color="inherit"
                      whiteSpace="nowrap"
                      width="100%"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      title={recommendedItem.type}
                    >
                      {recommendedItem.type}
                    </Typography>
                    <Typography
                      fontWeight={700}
                      fontSize={pxToEm(16)}
                      color="primary"
                      whiteSpace="nowrap"
                      width="100%"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      title={recommendedItem.name}
                    >
                      {recommendedItem.name}
                    </Typography>
                    <Box display="flex" alignItems="center">
                      {recommendedItem.isInStock && (
                        <CustomIcon size={0.75} dense name="overnight" />
                      )}
                      <Typography
                        fontWeight={300}
                        fontSize={pxToEm(14)}
                        color="inherit"
                        whiteSpace="nowrap"
                        width="100%"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        title={recommendedItem.manufacturer}
                      >
                        {recommendedItem.manufacturer}
                      </Typography>
                    </Box>
                  </Styled.MaterialInfo>
                  <Styled.ContainedCartButton
                    onClick={() => onAddToCart?.(recommendedItem)}
                    isLoading={recommendedItem.isCartLoading}
                    dense
                    isAddedToCart={recommendedItem.isAddedToCart}
                    iconName={
                      recommendedItem.isAddedToCart ? 'check' : 'cart-material'
                    }
                    color={
                      recommendedItem.isAddedToCart ? 'primary' : 'secondary'
                    }
                    size="large"
                  />
                </div>
              </div>
            ))}
          </Styled.RecommendedItemsContainer>
        </Styled.TopBannerContainer>
      )}
      {isProposalActive &&
        proposalType === 'service' &&
        proposalContentByType[proposalType]}
    </Styled.Dialog>
  );
};
